/**
 * The shared command-palette SURFACE.
 *
 * This component knows how a palette behaves -- named dialog, combobox,
 * listbox, active descendant and keyboard movement -- and deliberately knows
 * nothing about Solum data, routes, personas or permissions. Each host supplies
 * the already-governed items it can offer. That keeps a presentation primitive
 * from becoming a second authority model.
 *
 * -- THE CLOSE CONTROL IS NOT DECORATION (#1466 F-30B, ADR F-30 decision 5) ---
 * Escape closes, and that was the ONLY obvious exit. Measured: on a 375px phone
 * the panel fills every pixel, so there is no backdrop to tap either, and a
 * touch-only user has no Escape key -- selecting a destination was the only way
 * out of a dialog they may have opened by accident. The 960x480 automotive
 * viewport has the same hole (comment 42675 finding 1).
 *
 * It costs one control and zero lines in either host: `onClose` is already a
 * REQUIRED prop, so both bindings already hand this component the behaviour the
 * button needs.
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { CircleHelp, Clock3, Compass, FileText, MessageCircleQuestion, Search, Sparkles, Zap } from 'lucide-react'
import { Modal } from './controls/Modal'
import { SegmentedControl } from './controls/SegmentedControl'
import s from './CommandPalette.module.css'
import type { CommandMatchRange } from './commandRanking'

export interface CommandPaletteItem {
  id: string
  label: string
  /** Where the query matched the label; drawn underlined. `rankCommandGroups` fills it for help-aware hosts. */
  matches?: readonly CommandMatchRange[]
  detail?: string
  icon?: ReactNode
  testId?: string
  /** Presentation identity only. Hosts still decide admission and authority. */
  kind?: 'record' | 'action' | 'page' | 'ask' | 'recall' | 'help' | 'tour'
  /** The exact visible record this row represents, when it is a record. */
  record?: { entity: string; id: string; label: string }
  /** A refused action stays visible with its server-owned reason. */
  disabled?: boolean
  disabledReason?: string
  /** Forms and in-place acts may keep the palette mounted while they launch. */
  closeOnSelect?: boolean
  onSelect: () => void
}

export interface CommandPaletteGroup {
  id: string
  label: string
  items: CommandPaletteItem[]
  testId?: string
  /** `help`, `tours` and `suggested` opt a host into the help-aware intent table (`commandRanking.ts`). */
  kind?: 'records' | 'actions' | 'pages' | 'ask' | 'recall' | 'help' | 'tours' | 'suggested'
  /** Why this section leads for the current query ("You asked a question"). Set by the ranker. */
  reason?: string
}

/** The label with its matched spans underlined. Out-of-range or overlapping spans are ignored, never thrown. */
function markedLabel(label: string, matches: readonly CommandMatchRange[] | undefined): ReactNode {
  if (!matches?.length) return label
  const parts: ReactNode[] = []
  let at = 0
  for (const [start, end] of matches) {
    if (start < at || end <= start || end > label.length) continue
    if (start > at) parts.push(label.slice(at, start))
    parts.push(<span key={start} className={s.match} data-match="">{label.slice(start, end)}</span>)
    at = end
  }
  if (at < label.length) parts.push(label.slice(at))
  return parts
}

export interface CommandPaletteMode {
  id: string
  label: string
}

export interface CommandPaletteScopeHint {
  id: string
  label: string
  shortcut?: string
}

export interface CommandPaletteProps {
  label: string
  query: string
  onQueryChange: (query: string) => void
  onClose: () => void
  groups: CommandPaletteGroup[]
  placeholder?: string
  empty?: string
  pending?: boolean
  testId?: string
  modes?: CommandPaletteMode[]
  mode?: string
  onModeChange?: (mode: string) => void
  context?: ReactNode
  footer?: ReactNode
  onInputKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  /** Wide empty states may balance recall and creation without two listboxes. */
  split?: boolean
  onActiveItemChange?: (item: CommandPaletteItem | null) => void
  onScopeItem?: (item: CommandPaletteItem) => void
  /** Return true when Escape revoked narrowing and must not close the dialog. */
  onEscape?: () => boolean
  /** Compact vocabulary cues for the one field; they are hints, not modes. */
  scopeHints?: CommandPaletteScopeHint[]
}

export function CommandPalette({
  label, query, onQueryChange, onClose, groups, placeholder = 'Search...',
  empty = 'No results.', pending = false, testId, modes = [], mode,
  onModeChange, context, footer, onInputKeyDown, split = false,
  onActiveItemChange, onScopeItem, onEscape, scopeHints = [],
}: CommandPaletteProps) {
  const input = useRef<HTMLInputElement>(null)
  const results = useRef<HTMLDivElement>(null)
  const optionNodes = useRef(new Map<string, HTMLButtonElement>())
  const uid = useId().replace(/:/g, '')
  const listId = `command-palette-${uid}`
  const flat = useMemo(() => groups.flatMap((group) => group.items.map((item) => ({ item, groupId: group.id }))), [groups])
  const [activeId, setActiveId] = useState<string | null>(null)
  const active = Math.max(0, flat.findIndex(({ item }) => item.id === activeId))

  /* A new query or mode starts at its first answer. `flat` is deliberately not
     a dependency: async enrichment may replace the result array while the
     person is moving through it, and the stable item id must survive that. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setActiveId(flat[0]?.item.id ?? null) }, [query, mode])
  useEffect(() => {
    if (activeId !== null && flat.some(({ item }) => item.id === activeId)) return
    setActiveId(flat[0]?.item.id ?? null)
  }, [activeId, flat])
  const activeItem = flat[active]?.item ?? null
  useEffect(() => { onActiveItemChange?.(activeItem) }, [activeItem, onActiveItemChange])
  useEffect(() => {
    if (!activeItem) return
    const scroller = results.current
    const node = optionNodes.current.get(activeItem.id)
    if (!scroller || !node) return
    const above = node.offsetTop < scroller.scrollTop
    const below = node.offsetTop + node.offsetHeight > scroller.scrollTop + scroller.clientHeight
    if (above) scroller.scrollTop = node.offsetTop
    else if (below) scroller.scrollTop = node.offsetTop + node.offsetHeight - scroller.clientHeight
  }, [activeItem])

  const choose = (item: CommandPaletteItem | undefined) => {
    if (!item || item.disabled) return
    item.onSelect()
    if (item.closeOnSelect !== false) onClose()
  }

  const move = (next: number) => setActiveId(flat[(next + flat.length) % flat.length]?.item.id ?? null)

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onInputKeyDown?.(event)
    if (event.defaultPrevented) return
    if (event.key === 'Escape' && onEscape?.()) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (event.key === 'Tab' && !event.shiftKey && activeItem?.kind === 'record' && onScopeItem) {
      event.preventDefault()
      onScopeItem(activeItem)
      return
    }
    if (!flat.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      move(active + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      move(active - 1)
    } else if (split && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault()
      const groupIds = groups.filter((group) => group.items.length > 0).map((group) => group.id)
      const currentGroup = flat[active]?.groupId
      const groupIndex = Math.max(0, groupIds.indexOf(currentGroup ?? ''))
      const nextGroup = groupIds[(groupIndex + (event.key === 'ArrowRight' ? 1 : -1) + groupIds.length) % groupIds.length]
      const next = flat.find(({ groupId }) => groupId === nextGroup)
      setActiveId(next?.item.id ?? activeId)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveId(flat[0]?.item.id ?? null)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveId(flat.at(-1)?.item.id ?? null)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(activeItem ?? undefined)
    }
  }

  let index = -1
  return (
    <Modal label={label} onClose={onClose} initialFocus={input} testid={testId}
      className={s.panel} overlayClassName={s.overlay}>
      {modes.length > 0 && mode && onModeChange && (
        <div className={s.modes}>
          <SegmentedControl options={modes.map((item) => ({ value: item.id, label: item.label }))}
            value={mode} onChange={onModeChange} label="Command mode" />
        </div>
      )}
      <div className={s.inputRow}>
        <Search className={s.searchIcon} size={22} aria-hidden />
        {context}
        <input
          ref={input}
          className={s.input}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={flat[active] ? `${listId}-option-${active}` : undefined}
          value={query}
          onChange={(event) => onQueryChange(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
        />
        {/* Last in the row, so it is the last tab stop before the list rather
            than something a keyboard user must pass through to reach the input.
            Labelled, not a bare glyph: an icon-only exit in a full-screen sheet
            is the affordance a first-time touch user is looking for. */}
        <button type="button" className={s.close} data-testid="command-palette-close"
          aria-label="Close" onClick={onClose}>
          <kbd>esc</kbd>
        </button>
      </div>
      {scopeHints.length > 0 && (
        <div className={s.scopeHints} role="group" aria-label="Command search scopes" data-testid="command-scope-hints">
          <span className={s.scopeLabel}>Narrow</span>
          {scopeHints.map((hint) => (
            <span key={hint.id} className={s.scopeHint} data-scope={hint.id}>
              {hint.label}{hint.shortcut && <kbd>{hint.shortcut}</kbd>}
            </span>
          ))}
        </div>
      )}
      <div className={s.results} ref={results}>
        <div id={listId} className={s.list} data-split={split || undefined}
          role="listbox" aria-label={mode ? `${mode} results` : 'Results'}>
          {groups.map((group) => {
            if (!group.items.length) return null
            const headingId = `${listId}-group-${group.id}`
            return (
              <div key={group.id} role="group" aria-labelledby={headingId} data-testid={group.testId}>
                <div id={headingId} className={s.heading}>
                  <span>{group.label}</span>
                  {group.reason && <span className={s.leadReason} data-testid="command-lead-reason">{group.reason}</span>}
                  <span className={s.groupCount}>{group.items.length}</span>
                </div>
                {group.items.map((item) => {
                  index += 1
                  const itemIndex = index
                  const selected = item.id === activeId
                  return (
                    <button
                      ref={(node) => { if (node) optionNodes.current.set(item.id, node); else optionNodes.current.delete(item.id) }}
                      key={item.id}
                      type="button"
                      id={`${listId}-option-${itemIndex}`}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={item.disabled || undefined}
                      className={s.item}
                      data-selected={selected || undefined}
                      data-kind={item.kind}
                      data-testid={item.testId}
                      onPointerMove={() => setActiveId(item.id)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choose(item)}
                    >
                      <span className={s.itemIcon} aria-hidden>{item.icon ?? (
                        item.kind === 'action' ? <Zap />
                          : item.kind === 'ask' ? <MessageCircleQuestion />
                            : item.kind === 'help' ? <CircleHelp />
                              : item.kind === 'tour' ? <Compass />
                                : item.kind === 'page' ? <FileText />
                                  : item.kind === 'record' ? <Search />
                                    : item.kind === 'recall' ? <Clock3 /> : <Sparkles />
                      )}</span>
                      <span className={s.itemLabel}>{markedLabel(item.label, item.matches)}</span>
                      {(item.disabledReason || item.detail) && <span className={s.detail}>{item.disabledReason ?? item.detail}</span>}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
        {/*
          A SIBLING of the listbox, and this is a real defect rather than a tidy-up.
          `role="listbox"` admits `option` and `group` children and nothing else,
          so a `role="status"` inside it is an `aria-required-children` violation
          — axe's words: "Element has children which are not allowed: [role=status]".

          It had never fired, because neither host could reach an EMPTY palette:
          the console always opened onto pins and recents, and the portal opened
          onto its whole 24-page catalogue. #1466 F-30C ruling 7 gates that
          catalogue behind a non-empty query, which makes "opened, nothing to show
          yet" the ordinary first frame in the portal — and `ci / a11y` went red on
          the first browser run. The bug is the shared component's and predates the
          gate; the gate is only what made it reachable.

          It stays a live region, and it stays immediately after the list in the
          reading order. The shared results wrapper preserves the one visual region
          the status occupied before this semantic repair; otherwise the empty
          list's minimum height and the status stack, growing the dialog by 71px.
        */}
        {pending && <div className={s.status} role="status">Searching...</div>}
        {!pending && flat.length === 0 && <div className={s.status} role="status">{empty}</div>}
      </div>
      <div className={s.footer}>
        <div className={s.hostFooter}>{footer}</div>
        <div className={s.keys} aria-hidden="true">
          <span>up/down move</span><span>enter open</span><span>esc close</span>
        </div>
      </div>
    </Modal>
  )
}
